import {FormControl, FormField, isCurrency} from 'platform/components';
import {Grid, GridItem, Heading, Show, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useWatch} from 'react-hook-form';

import {always} from 'ramda';

import {CustomerResponseBodyV2} from '@dms/api/customer';
import {GetSupplierDiscountGroupsResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {DEFAULT_CURRENCY, getOptionsFromCustomersAsSupplier} from '@dms/shared';
import {getOptionsFromSupplierDiscountGroups} from '@dms/warehouse/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SupplierArticlesAddNewForm} from '../types/SupplierArticlesAddNewForm';

interface SupplierArticlesAddNewSupplierInfoFormProps extends RequiredTestIdProps {
  control: FormControl<SupplierArticlesAddNewForm>;
  suppliers: CustomerResponseBodyV2[] | Nullish;
  supplierDiscountGroups: GetSupplierDiscountGroupsResponse | Nullish;
  isEditing?: boolean;
}

export function SupplierArticlesAddNewSupplierInfoForm(
  props: SupplierArticlesAddNewSupplierInfoFormProps
) {
  const [currency, recommendedPriceSource, purchasePriceSource] = useWatch({
    control: props.control,
    name: [
      'supplierInfo.currency',
      'supplierInfo.recommendedPriceSource',
      'supplierInfo.purchasePriceSource',
    ],
  });

  const recommendedPriceSourceHelperText = match(recommendedPriceSource)
    .with(Pattern.nullish, always(undefined))
    .with(
      'DELIVERY_NOTE',
      always(
        i18n.t('entity.warehouse.labels.recommendedPriceSource', {
          source: i18n.t('entity.warehouse.labels.deliveryNote'),
        })
      )
    )
    .with(
      'PRICE_LIST',
      always(
        i18n.t('entity.warehouse.labels.recommendedPriceSource', {
          source: i18n.t('entity.warehouse.labels.supplierPriceList'),
        })
      )
    )
    .with(
      'MANUAL',
      always(
        i18n.t('entity.warehouse.labels.recommendedPriceSource', {
          source: i18n.t('entity.warehouse.labels.manuallySet'),
        })
      )
    )
    .otherwise(always(undefined));

  const purchasePriceSourceHelperText = match(purchasePriceSource)
    .with(Pattern.nullish, always(undefined))
    .with(
      'DELIVERY_NOTE',
      always(
        i18n.t('entity.warehouse.labels.purchasePriceSource', {
          source: i18n.t('entity.warehouse.labels.deliveryNote'),
        })
      )
    )
    .with(
      'PRICE_LIST',
      always(
        i18n.t('entity.warehouse.labels.purchasePriceSource', {
          source: i18n.t('entity.warehouse.labels.supplierPriceList'),
        })
      )
    )
    .with(
      'MANUAL',
      always(
        i18n.t('entity.warehouse.labels.purchasePriceSource', {
          source: i18n.t('entity.warehouse.labels.manuallySet'),
        })
      )
    )
    .otherwise(always(undefined));

  return (
    <VStack spacing={4} data-testid={props['data-testid']}>
      <Heading size={5}>{i18n.t('entity.warehouse.labels.supplierInfo')}</Heading>
      <Grid columns={4} spacing={4}>
        <GridItem span={1}>
          <FormField
            isRequired
            isDisabled
            control={props.control}
            name="supplierInfo.supplierId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.supplier')}
            options={getOptionsFromCustomersAsSupplier(props.suppliers)}
            data-testid={suffixTestId('inputs.supplierId', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            name="supplierInfo.supplierOrderingNumber"
            type="text"
            label={i18n.t('entity.warehouse.labels.supplierOrderingNumber')}
            data-testid={suffixTestId('inputs.supplierOrderingNumber', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            name="supplierInfo.supplierBulkPackageQuantity"
            type="number"
            label={i18n.t('entity.warehouse.labels.supplierBulkPackageQuantity')}
            decimalPlaces={4}
            data-testid={suffixTestId('inputs.supplierBulkPackageQuantity', props)}
          />
        </GridItem>
        <Show when={props.isEditing}>
          <GridItem span={1}>
            <FormField
              isDisabled
              control={props.control}
              name="supplierInfo.supplierDiscountGroupCode"
              type="choice"
              label={i18n.t('entity.warehouse.labels.supplierDiscountGroup')}
              options={getOptionsFromSupplierDiscountGroups(props.supplierDiscountGroups)}
              data-testid={suffixTestId('inputs.supplierDiscountGroup', props)}
            />
          </GridItem>
        </Show>
        <GridItem span={1}>
          <FormField
            isDisabled
            control={props.control}
            name="supplierInfo.supplierManufacturerName"
            type="text"
            label={i18n.t('entity.warehouse.labels.supplierManufacturerName')}
            data-testid={suffixTestId('inputs.supplierManufacturerName', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            isDisabled
            control={props.control}
            name="supplierInfo.supplierManufacturerCode"
            type="text"
            label={i18n.t('entity.warehouse.labels.supplierManufacturerCode')}
            data-testid={suffixTestId('inputs.supplierManufacturerCode', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            isDisabled
            control={props.control}
            name="supplierInfo.recommendedPriceWithoutVat"
            type="currency"
            label={i18n.t('entity.warehouse.labels.recommendedPriceWithoutVat')}
            currency={isCurrency(currency) ? currency : DEFAULT_CURRENCY}
            helperText={recommendedPriceSourceHelperText}
            data-testid={suffixTestId('inputs.recommendedPriceWithoutVat', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            isDisabled
            control={props.control}
            name="supplierInfo.purchasePriceWithoutVat"
            type="currency"
            label={i18n.t('entity.warehouse.labels.purchasePriceWithoutVat')}
            currency={isCurrency(currency) ? currency : DEFAULT_CURRENCY}
            helperText={purchasePriceSourceHelperText}
            data-testid={suffixTestId('inputs.purchasePriceWithoutVat', props)}
          />
        </GridItem>
      </Grid>
    </VStack>
  );
}
